import { useContext, useState } from "react"
import Grid from "@mui/material/Grid2"
import { Button } from "@mui/material"
import { rootContext } from "../../contexts/rootContext"
import { estimateTripDurationLabel, getPointsOfInterestForTrip, TripPointOfInterest } from "../../utilities/tripHelper"
import { pointOfInterestIconsByType } from "../../utilities/buffaloArcGisHelper"

// Long trips run to dozens of points, which buries the Directions section below
// them, so only the first stretch is shown until the list is expanded. Collapsing
// only pays for itself once it hides a few rows — putting one row behind a button
// is just a second click for nothing.
const COLLAPSED_COUNT = 6
const MIN_HIDDEN_TO_COLLAPSE = 3

// Anything this close to the put-in is described as being at it rather than given
// a mileage, since a tenth of a mile is inside the error of snapping a point to
// the river path anyway.
const AT_PUT_IN_MILES = 0.05

interface TripPointsOfInterestProps {
    accessPoint1: string
    accessPoint2: string
}

const getSubtitle = (pointOfInterest: TripPointOfInterest) => {
    const sideLabel = pointOfInterest.side !== undefined ? `River ${pointOfInterest.side}` : null
    const positionLabel = pointOfInterest.milesFromPutIn < AT_PUT_IN_MILES
        ? "At the put-in"
        : `About ${estimateTripDurationLabel(pointOfInterest.milesFromPutIn)} in`

    return [sideLabel, positionLabel].filter(label => label !== null).join(" · ")
}

export const TripPointsOfInterest = (props: TripPointsOfInterestProps) => {
    const context = useContext(rootContext)
    const [isExpanded, setIsExpanded] = useState(false)

    const pointsOfInterest = getPointsOfInterestForTrip(props.accessPoint1, props.accessPoint2)
    if (pointsOfInterest.length === 0) return null

    const isCollapsible = pointsOfInterest.length >= COLLAPSED_COUNT + MIN_HIDDEN_TO_COLLAPSE
    const visiblePointsOfInterest = isCollapsible && !isExpanded
        ? pointsOfInterest.slice(0, COLLAPSED_COUNT)
        : pointsOfInterest

    return (
        <>
            <Grid size={12} sx={{ fontWeight: "bold", fontSize: "18px", color: context.theme.text.contrast5 }}>
                Along the Way
            </Grid>
            <Grid
                container
                size={12}
                sx={{
                    backgroundColor: "#1a1a1a",
                    borderRadius: "10px",
                    padding: "10px 15px"
                }}
            >
                {visiblePointsOfInterest.map((pointOfInterest, index) => (
                    <Grid
                        container
                        size={12}
                        spacing={1.5}
                        key={pointOfInterest.id}
                        sx={{ alignItems: "flex-start", flexWrap: "nowrap", padding: "8px 0" }}
                    >
                        <Grid sx={{ width: "52px", flexShrink: 0, textAlign: "right", paddingTop: "3px" }}>
                            <span style={{ fontSize: "13px", color: context.theme.text.contrast4 }}>
                                {pointOfInterest.milesFromPutIn.toFixed(1)} mi
                            </span>
                        </Grid>
                        {/* The icons are the same glyphs the map uses, so a point picked out
                            on the map is recognizable in this list without a legend. */}
                        <Grid
                            sx={{
                                flexShrink: 0,
                                // Draws the connecting line behind the icon, stopping at the last
                                // row so the list doesn't trail off into the padding below it.
                                position: "relative",
                                "&::before": index === visiblePointsOfInterest.length - 1 ? undefined : {
                                    content: '""',
                                    position: "absolute",
                                    left: "11px",
                                    top: "22px",
                                    bottom: "-18px",
                                    borderLeft: "1px dashed rgba(255,255,255,0.2)"
                                }
                            }}
                        >
                            <img
                                src={pointOfInterestIconsByType[pointOfInterest.type]}
                                alt=""
                                width={22}
                                height={22}
                                style={{ display: "block" }}
                            />
                        </Grid>
                        <Grid size="grow" sx={{ minWidth: 0 }}>
                            <div style={{ overflowWrap: "anywhere" }}>{pointOfInterest.name}</div>
                            <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.55)" }}>
                                {getSubtitle(pointOfInterest)}
                            </div>
                            {pointOfInterest.description !== undefined &&
                                <div style={{ fontSize: "13px", marginTop: "2px" }}>
                                    {pointOfInterest.description}
                                </div>
                            }
                        </Grid>
                    </Grid>
                ))}
                {isCollapsible &&
                    <Grid size={12} sx={{ textAlign: "center" }}>
                        <Button
                            size="small"
                            onClick={() => setIsExpanded(!isExpanded)}
                            sx={{
                                color: context.theme.text.contrast4,
                                textTransform: "none",
                                cursor: `url(${context.theme.cursor.pointer}), pointer`,
                                "&:hover": { backgroundColor: context.theme.misc.buttonHover }
                            }}
                        >
                            {isExpanded ? "Show fewer" : `Show all ${pointsOfInterest.length}`}
                        </Button>
                    </Grid>
                }
            </Grid>
            <Grid size={12} sx={{ fontSize: "12px", color: context.theme.text.contrast2, marginTop: "-12px" }}>
                Distances are measured down the river from the put-in. Left and right are as you face downstream.
            </Grid>
        </>
    )
}
